import styled from '@emotion/styled';
import Diem from '@travelnest/diem';
import { Button, PresetThemes } from '@travelnest/merlin.button';
import { DatePicker } from '@travelnest/merlin.date-picker';
import { InlineError } from '@travelnest/merlin.inline-message';
import { Spacer } from '@travelnest/merlin.spacer';
import theme from '@travelnest/merlin.theme';
import { Label } from '@travelnest/merlin.typography';
import React from 'react';
import { rangesToDates, stripTimezone } from './calendar-helpers';

interface IViewportProps {
	isMobile: boolean;
}
const DateSelectors = styled.div<IViewportProps>`
	display: flex;
	flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
	align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'flex-end')};
`;
const DateSelector = styled.div`
	margin-right: ${theme.layout.spacing.smaller};
	width: 100%;
`;

interface IDateRangeSelctorsProps {
	onCalSelectionChange: (param: Date[]) => void;
	setCurrentMonth: (m: Date) => any;
	breakpoint: string;
	hideFinalSpacing?: boolean;
}

export const DateRangeSelectors: React.FC<IDateRangeSelctorsProps> = ({
	onCalSelectionChange,
	setCurrentMonth,
	breakpoint,
	hideFinalSpacing,
}) => {
	const [selectedDateRangeStart, setSelectedDateRangeStart] = React.useState<
		Diem | undefined
	>(undefined);
	const [selectedDateRangeEnd, setSelectedDateRangeEnd] = React.useState<
		Diem | undefined
	>(undefined);
	const startDateIsValid =
		selectedDateRangeStart &&
		selectedDateRangeEnd &&
		selectedDateRangeStart.diff(selectedDateRangeEnd) <= 0;
	const endDateIsValid =
		selectedDateRangeStart &&
		selectedDateRangeEnd &&
		selectedDateRangeEnd.diff(selectedDateRangeStart) >= 0;
	const selectedDateRangeIsInvalid =
		selectedDateRangeStart &&
		!startDateIsValid &&
		selectedDateRangeEnd &&
		!endDateIsValid;
	return (
		<>
			<DateSelectors isMobile={breakpoint === 'mobile'}>
				<DateSelector>
					<Label htmlFor="selected-date-range-start">
						Start date
					</Label>
					<Spacer spacing="smallest" />
					<DatePicker
						id="selected-date-range-start"
						value={selectedDateRangeStart}
						onChange={(d) => {
							setSelectedDateRangeStart(d);
							// also set end date by default to guarantee the date picker
							// of end date will open at the same month
							setSelectedDateRangeEnd(d);
						}}
						valid={startDateIsValid}
					/>
				</DateSelector>
				<Spacer spacing="small" />
				<DateSelector>
					<Label htmlFor="selected-date-range-end">End date</Label>
					<Spacer spacing="smallest" />
					<DatePicker
						id="selected-date-range-end"
						onChange={setSelectedDateRangeEnd}
						value={selectedDateRangeEnd}
						valid={endDateIsValid}
						minDate={
							selectedDateRangeStart?.toString() ?? undefined
						}
					/>
				</DateSelector>
				<Spacer spacing="small" />
				<Button
					buttonType={PresetThemes.Primary}
					onClick={() => {
						const startDate = stripTimezone(
							selectedDateRangeStart!
						);
						const endDate = stripTimezone(selectedDateRangeEnd!);
						const dateRange = rangesToDates([[startDate, endDate]]);
						onCalSelectionChange(dateRange);

						// Set the current Calendar month to the month of the start-month of the date range
						const newMonth = new Date();
						newMonth.setMonth(startDate.getMonth());
						newMonth.setFullYear(startDate.getFullYear());
						setCurrentMonth(newMonth);

						// Scroll to have Calendar itself in viewport to show the new selection
						window.scrollTo(0, 0);

						// Clear selections
						setSelectedDateRangeStart(undefined);
						setSelectedDateRangeEnd(undefined);
					}}
					disabled={
						!selectedDateRangeStart ||
						!selectedDateRangeEnd ||
						selectedDateRangeIsInvalid
					}
				>
					Select
				</Button>
			</DateSelectors>
			<Spacer spacing="smaller" />
			{selectedDateRangeIsInvalid && (
				<InlineError text="Start date should come before end date." />
			)}
			{!hideFinalSpacing && <Spacer />}
		</>
	);
};
